'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useCartStore } from 'components/shared/Cart/store/CartStoreContext';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';
import type { SddDeliveryBanner } from 'content/campaigns';
import { SDD_GTM_EVENTS, SDD_PINCODE_STORAGE_KEY } from './config';
import {
  fetchSddServiceability,
  IN_PINCODE_RE,
  type SddDeliveryMode,
  type SddServiceabilityData,
} from './api';

type SddTranslator = (key: string, values?: Record<string, string>) => string;

const TICK_MS = 60_000;
const SAME_DAY_END_MINUTES = 11 * 60;
const TWO_HOUR_END_MINUTES = 19 * 60;

export type SddArrivesWhen = 'today' | 'tomorrow';
export type SddDeliveryWindow = SddDeliveryMode;
export type SddSelectionBlocker = 'engraving' | 'open_size';

type BannerFields = {
  bannerTitle: string;
  cta: string;
  placeholder?: string;
  bannerAdvisory?: string;
  orderBy?: string;
};

type WindowFields = {
  deliveryWindow: SddDeliveryWindow;
};

type HeldFields = {
  hadEligible: boolean;
  heldCheckoutEta?: string;
  heldHeroDate?: string;
  heldHeroPincode?: string;
  heldHeroWhen?: SddArrivesWhen;
  heldHeroTime?: string;
  heldEtaWindow?: SddDeliveryWindow;
};

export type SddNddState =
  | { status: 'hidden' }
  | ({
      status: 'idle';
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields)
  | ({
      status: 'editing';
      pincode: string;
      fieldError?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields)
  | ({
      status: 'loading';
      pincode: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields)
  | ({
      status: 'eligible';
      pincode: string;
      title: string;
      heroDate?: string;
      heroWhen?: SddArrivesWhen;
      heroTime?: string;
      etaWindow: SddDeliveryWindow;
      advisory?: string;
      mode?: SddDeliveryMode;
      city?: string;
      cutoffIst?: string;
      etaDate?: string;
      orderBy?: string;
      getItBy?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & WindowFields)
  | ({
      status: 'ineligible';
      pincode: string;
      title: string;
      city?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields)
  | ({
      status: 'error';
      pincode: string;
      message: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields);

export type SddNddController = {
  state: SddNddState;
  startCheck: () => void;
  setPincodeInput: (value: string) => void;
  submitPincode: () => void;
  changePincode: () => void;
};

/** IST clock window. Used to refetch at 11:00 / 19:00 / midnight and as
 * fallback when campaigns / serviceability omit `mode`. 11:00 inclusive
 * starts TWO_HOUR; 19:00 inclusive starts NEXT_DAY. */
export const resolveSddWindow = (now: Date = new Date()): SddDeliveryWindow => {
  const { minutesOfDay } = getIstParts(now);
  if (minutesOfDay < SAME_DAY_END_MINUTES) return 'same_day';
  if (minutesOfDay < TWO_HOUR_END_MINUTES) return 'two_hour';
  return 'next_day';
};

export const sddWindowShowsTimer = (window: SddDeliveryWindow): boolean =>
  window !== 'next_day';

/** API `order_by` when present (idle banner or eligible result). */
export const sddTimerDeadline = (state: SddNddState): string | undefined => {
  if (state.status === 'hidden' || state.status === 'ineligible') {
    return undefined;
  }
  return 'orderBy' in state ? state.orderBy : undefined;
};

/** Warehouse "Dispatched by" only before an eligible result, or after ineligible.
 * Change pincode after eligible keeps warehouse hidden.
 * First-check loading / error do not hide it — the slot reserves the extra
 * meta line so offers do not jump. */
export function sddReplacesWarehouseEstimate(state: SddNddState): boolean {
  if (state.status === 'eligible') return true;
  if (
    state.status === 'editing' ||
    state.status === 'loading' ||
    state.status === 'error'
  ) {
    return state.hadEligible;
  }
  return false;
}

/** Compact ETA for cart summary + bottom bar. Holds last eligible copy
 * through Change pincode until a new ineligible result. Engraving: warehouse. */
export function sddCheckoutEta(state: SddNddState): string | null {
  if (state.status === 'hidden') return null;
  if (state.selectionBlocker === 'engraving') return null;
  if (state.status === 'eligible') return state.getItBy ?? null;
  if (
    (state.status === 'editing' ||
      state.status === 'loading' ||
      state.status === 'error') &&
    state.hadEligible
  ) {
    return state.heldCheckoutEta ?? null;
  }
  return null;
}

/** Buy-section `.shipping-estimate` line: warehouse otherwise. Holds through Change pincode. */
export function sddHeroTimeline(state: SddNddState): {
  date: string;
  pincode: string;
  when: SddArrivesWhen;
  time?: string;
  window: SddDeliveryWindow;
} | null {
  if (state.status === 'eligible') {
    const window = state.etaWindow;
    if (window === 'two_hour') {
      return {
        date: state.heroDate ?? '',
        pincode: state.pincode,
        when: state.heroWhen ?? 'today',
        window,
        ...(state.heroTime ? { time: state.heroTime } : {}),
      };
    }
    if (state.heroDate && state.heroWhen) {
      return {
        date: state.heroDate,
        pincode: state.pincode,
        when: state.heroWhen,
        window,
      };
    }
    return null;
  }
  if (
    (state.status === 'editing' ||
      state.status === 'loading' ||
      state.status === 'error') &&
    state.hadEligible &&
    state.heldEtaWindow &&
    state.heldHeroPincode
  ) {
    const window = state.heldEtaWindow;
    if (window === 'two_hour') {
      return {
        date: state.heldHeroDate ?? '',
        pincode: state.heldHeroPincode,
        when: state.heldHeroWhen ?? 'today',
        window,
        ...(state.heldHeroTime ? { time: state.heldHeroTime } : {}),
      };
    }
    if (state.heldHeroDate && state.heldHeroWhen) {
      return {
        date: state.heldHeroDate,
        pincode: state.heldHeroPincode,
        when: state.heldHeroWhen,
        window,
      };
    }
  }
  return null;
}

export const idleSddNddController: SddNddController = {
  state: { status: 'hidden' },
  startCheck: () => undefined,
  setPincodeInput: () => undefined,
  submitPincode: () => undefined,
  changePincode: () => undefined,
};

type Phase =
  | { kind: 'idle' }
  | { kind: 'editing'; pincode: string; fieldError?: string }
  | { kind: 'loading'; pincode: string }
  | { kind: 'result'; data: SddServiceabilityData }
  | { kind: 'error'; pincode: string; message: string };

type IstParts = { isoDate: string; minutesOfDay: number };

export const getIstParts = (date: Date): IstParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    minutesOfDay: Number(get('hour')) * 60 + Number(get('minute')),
  };
};

export const parseCutoffMinutes = (cutoffIst: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(cutoffIst.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
};

const parseOrderByMs = (value: string | undefined): number | null => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

const formatEtaDate = (etaDate: string, locale: string): string | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(etaDate)) return undefined;
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).formatToParts(new Date(`${etaDate}T00:00:00Z`));
  const day = parts.find((part) => part.type === 'day')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  if (!day || !month) return undefined;
  return `${day} ${month}`;
};

/** "2026-09-02T15:00:00+05:30" → "3 PM" (IST). Minutes kept when non-zero. */
export const formatDeliveryByTime = (
  deliveryBy: string | undefined,
): string | undefined => {
  if (!deliveryBy) return undefined;
  const instant = new Date(deliveryBy);
  if (Number.isNaN(instant.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').replace(/\./g, '').toUpperCase();
  if (!hour || !dayPeriod) return undefined;
  if (minute === '00') return `${hour} ${dayPeriod}`;
  return `${hour}:${minute} ${dayPeriod}`;
};

const arrivesCheckoutCopy = (
  t: SddTranslator,
  window: SddDeliveryWindow,
  when: SddArrivesWhen,
  date: string,
  time?: string,
): string => {
  if (window === 'two_hour') {
    return time
      ? `${t('ring.sddNdd.arrivesLead')} ${t('ring.sddNdd.byTime', { time })}`
      : t('ring.sddNdd.arrivesInTwoHours');
  }
  /** Literal keys, not a ternary key — check-i18n-keys only sees quoted first args. */
  return when === 'today'
    ? t('ring.sddNdd.arrivesToday', { date })
    : t('ring.sddNdd.arrivesTomorrow', { date });
};

type EligiblePresentation = {
  heroDate?: string;
  heroWhen?: SddArrivesWhen;
  heroTime?: string;
  getItBy?: string;
  etaWindow: SddDeliveryWindow;
};

const presentEligible = (
  data: SddServiceabilityData,
  locale: string,
  t: SddTranslator,
  now: Date,
): EligiblePresentation => {
  const etaWindow = data.mode ?? resolveSddWindow(now);
  const date = data.eta_date ? formatEtaDate(data.eta_date, locale) : undefined;
  if (etaWindow === 'two_hour') {
    const heroTime = formatDeliveryByTime(data.delivery_by);
    return {
      heroDate: date,
      heroWhen: 'today',
      ...(heroTime ? { heroTime } : {}),
      getItBy: arrivesCheckoutCopy(t, etaWindow, 'today', date ?? '', heroTime),
      etaWindow,
    };
  }
  const heroWhen: SddArrivesWhen =
    etaWindow === 'next_day' ? 'tomorrow' : 'today';
  return {
    heroDate: date,
    heroWhen,
    getItBy: date
      ? arrivesCheckoutCopy(t, etaWindow, heroWhen, date)
      : undefined,
    etaWindow,
  };
};

const sanitizePincode = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, 6);

const pinFromPhase = (phase: Phase): string | null => {
  if (phase.kind === 'result') return phase.data.pincode;
  if (
    phase.kind === 'editing' ||
    phase.kind === 'loading' ||
    phase.kind === 'error'
  ) {
    return phase.pincode || null;
  }
  return null;
};

const storedSddPincode = (phasePin: string | null): string | null => {
  if (phasePin && IN_PINCODE_RE.test(phasePin)) return phasePin;
  try {
    const stored = sessionStorage.getItem(SDD_PINCODE_STORAGE_KEY);
    return stored && IN_PINCODE_RE.test(stored) ? stored : null;
  } catch {
    return null;
  }
};

const pushToDataLayer = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') return;
  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({ event, ...data });
  } catch {}
};

const bannerFields = (banner: SddDeliveryBanner): BannerFields => ({
  bannerTitle: banner.title,
  cta: banner.cta,
  placeholder: banner.placeholder,
  bannerAdvisory: banner.advisory,
  ...(banner.order_by ? { orderBy: banner.order_by } : {}),
});

export function useSddNddEligibility(args: {
  engravingSelected: boolean;
  isOpenSize: boolean;
}): SddNddController {
  // Module constant — same branch every render in a given build.
  if (!SDD_NDD_BADGE_ENABLED) return idleSddNddController;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- compile-time flag
  return useSddNddEligibilityRuntime(args);
}

function useSddNddEligibilityRuntime(args: {
  engravingSelected: boolean;
  isOpenSize: boolean;
}): SddNddController {
  const { engravingSelected, isOpenSize } = args;
  // main read these off CartContext; this branch's cart state lives in Zustand.
  const sddDelivery = useCartStore((s) => s.sddDelivery);
  const refetchCampaigns = useCartStore((s) => s.refetchCampaigns);
  const t = useTranslations('ring') as unknown as SddTranslator;
  const locale = useLocale();

  const selectionBlocker: SddSelectionBlocker | undefined = engravingSelected
    ? 'engraving'
    : isOpenSize
      ? 'open_size'
      : undefined;

  const enabled = SDD_NDD_BADGE_ENABLED && sddDelivery !== null;
  const banner = enabled ? sddDelivery : null;

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [heldEligible, setHeldEligible] = useState<{
    hadEligible: boolean;
    checkoutEta?: string;
    heroDate?: string;
    heroPincode?: string;
    heroWhen?: SddArrivesWhen;
    heroTime?: string;
    etaWindow?: SddDeliveryWindow;
  }>({ hadEligible: false });
  const [deliveryWindow, setDeliveryWindow] = useState(resolveSddWindow);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const lastResultRef = useRef<SddServiceabilityData | null>(null);
  const restoredRef = useRef(false);
  const shownRef = useRef(false);
  const cutoffRefetchRef = useRef(false);
  const windowRef = useRef<SddDeliveryWindow>(deliveryWindow);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const rememberEligible = useCallback(
    (data: SddServiceabilityData) => {
      if (!data.eligible) {
        setHeldEligible({ hadEligible: false });
        return;
      }
      const presented = presentEligible(data, locale, t, new Date());
      setHeldEligible({
        hadEligible: true,
        checkoutEta: presented.getItBy,
        heroPincode: data.pincode,
        etaWindow: presented.etaWindow,
        ...(presented.heroDate ? { heroDate: presented.heroDate } : {}),
        ...(presented.heroWhen ? { heroWhen: presented.heroWhen } : {}),
        ...(presented.heroTime ? { heroTime: presented.heroTime } : {}),
      });
    },
    [locale, t],
  );

  const runCheck = useCallback(
    async (pincode: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;
      const isStale = () =>
        controller.signal.aborted || requestId !== requestIdRef.current;
      setPhase({ kind: 'loading', pincode });
      pushToDataLayer(SDD_GTM_EVENTS.CHECK, { pincode });
      try {
        const result = await fetchSddServiceability(pincode, controller.signal);
        if (isStale()) return;
        if (!result.ok) {
          if (result.code === 'invalid_pincode') {
            setPhase({
              kind: 'editing',
              pincode,
              fieldError: result.message || t('ring.sddNdd.invalidPincode'),
            });
            return;
          }
          setPhase({
            kind: 'error',
            pincode,
            message: result.message || t('ring.sddNdd.networkError'),
          });
          pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
            eligible: false,
            reason: result.code,
            pincode,
          });
          return;
        }
        try {
          sessionStorage.setItem(SDD_PINCODE_STORAGE_KEY, pincode);
        } catch {}
        // Re-arm the cutoff watcher only when the fresh deadline is still ahead.
        // A result whose order_by (or cutoff_ist) already passed must stay
        // disarmed or the effect refetches in a loop.
        const orderByMs = parseOrderByMs(result.data.order_by);
        if (orderByMs != null) {
          cutoffRefetchRef.current = Date.now() >= orderByMs;
        } else {
          const nextCutoff = result.data.cutoff_ist
            ? parseCutoffMinutes(result.data.cutoff_ist)
            : null;
          cutoffRefetchRef.current =
            nextCutoff != null &&
            getIstParts(new Date()).minutesOfDay >= nextCutoff;
        }
        rememberEligible(result.data);
        lastResultRef.current = result.data;
        setPhase({ kind: 'result', data: result.data });
        pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
          eligible: result.data.eligible,
          mode: result.data.mode,
          city: result.data.city,
          pincode,
        });
      } catch {
        if (isStale()) return;
        setPhase({
          kind: 'error',
          pincode,
          message: t('ring.sddNdd.networkError'),
        });
        pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
          eligible: false,
          reason: 'network',
          pincode,
        });
      }
    },
    [t, rememberEligible],
  );

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!banner || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const stored = sessionStorage.getItem(SDD_PINCODE_STORAGE_KEY);
      if (stored && IN_PINCODE_RE.test(stored)) {
        void runCheck(stored);
      }
    } catch {}
  }, [banner, runCheck]);

  useEffect(() => {
    if (!banner) return;
    const tick = () => {
      const nextWindow = resolveSddWindow();
      if (windowRef.current !== nextWindow) {
        windowRef.current = nextWindow;
        setDeliveryWindow(nextWindow);
        refetchCampaigns();
        const pin = storedSddPincode(pinFromPhase(phaseRef.current));
        if (pin) void runCheck(pin);
        return;
      }
      const current = phaseRef.current;
      if (current.kind !== 'result' || !current.data.eligible) return;
      const resultMode = current.data.mode ?? resolveSddWindow();
      if (!sddWindowShowsTimer(resultMode)) return;
      if (cutoffRefetchRef.current) return;
      const orderByMs = parseOrderByMs(current.data.order_by);
      if (orderByMs != null) {
        if (Date.now() >= orderByMs) {
          cutoffRefetchRef.current = true;
          void runCheck(current.data.pincode);
        }
        return;
      }
      if (!current.data.cutoff_ist) return;
      const cutoff = parseCutoffMinutes(current.data.cutoff_ist);
      if (cutoff == null) return;
      if (getIstParts(new Date()).minutesOfDay >= cutoff) {
        cutoffRefetchRef.current = true;
        void runCheck(current.data.pincode);
      }
    };
    const id = setInterval(tick, TICK_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [banner, refetchCampaigns, runCheck]);

  const startCheck = useCallback(() => {
    setPhase({ kind: 'editing', pincode: '' });
  }, []);

  const setPincodeInput = useCallback(
    (value: string) => {
      const pincode = sanitizePincode(value);
      setPhase({ kind: 'editing', pincode });
      if (pincode.length === 6) {
        if (IN_PINCODE_RE.test(pincode)) {
          void runCheck(pincode);
        } else {
          setPhase({
            kind: 'editing',
            pincode,
            fieldError: t('ring.sddNdd.invalidPincode'),
          });
        }
      }
    },
    [runCheck, t],
  );

  // Accepts 'error' and ineligible result phases too: the card keeps the
  // pincode in the field there, and Check re-submits it.
  const submitPincode = useCallback(() => {
    const pincode =
      phase.kind === 'editing' || phase.kind === 'error'
        ? phase.pincode
        : phase.kind === 'result' && !phase.data.eligible
          ? phase.data.pincode
          : null;
    if (pincode === null) return;
    if (!IN_PINCODE_RE.test(pincode)) {
      setPhase({
        kind: 'editing',
        pincode,
        fieldError: t('ring.sddNdd.invalidPincode'),
      });
      return;
    }
    void runCheck(pincode);
  }, [phase, runCheck, t]);

  const changePincode = useCallback(() => {
    abortRef.current?.abort();
    const previous =
      phase.kind === 'editing' ||
      phase.kind === 'loading' ||
      phase.kind === 'error'
        ? phase.pincode
        : phase.kind === 'result'
          ? phase.data.pincode
          : '';
    setPhase({ kind: 'editing', pincode: previous });
  }, [phase]);

  const state: SddNddState = useMemo(() => {
    if (!banner) return { status: 'hidden' };
    const apiWindow: SddDeliveryWindow | undefined =
      (phase.kind === 'result' ? phase.data.mode : undefined) ?? banner.mode;
    const windowFields = { deliveryWindow: apiWindow ?? deliveryWindow };
    const selection = {
      appliesToSelection: !selectionBlocker,
      selectionBlocker,
    };
    const held = {
      hadEligible: heldEligible.hadEligible,
      ...(heldEligible.checkoutEta
        ? { heldCheckoutEta: heldEligible.checkoutEta }
        : {}),
      ...(heldEligible.etaWindow
        ? { heldEtaWindow: heldEligible.etaWindow }
        : {}),
      ...(heldEligible.heroPincode
        ? {
            heldHeroPincode: heldEligible.heroPincode,
            ...(heldEligible.heroDate
              ? { heldHeroDate: heldEligible.heroDate }
              : {}),
            ...(heldEligible.heroWhen
              ? { heldHeroWhen: heldEligible.heroWhen }
              : {}),
            ...(heldEligible.heroTime
              ? { heldHeroTime: heldEligible.heroTime }
              : {}),
          }
        : {}),
    };
    const bannerUi = bannerFields(banner);
    if (phase.kind === 'idle') {
      return { status: 'idle', ...bannerUi, ...selection, ...windowFields };
    }
    if (phase.kind === 'editing') {
      return {
        status: 'editing',
        pincode: phase.pincode,
        fieldError: phase.fieldError,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    if (phase.kind === 'loading') {
      return {
        status: 'loading',
        pincode: phase.pincode,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    if (phase.kind === 'error') {
      return {
        status: 'error',
        pincode: phase.pincode,
        message: phase.message,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    const { data } = phase;
    if (!data.eligible) {
      return {
        status: 'ineligible',
        pincode: data.pincode,
        title: data.title,
        city: data.city,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    return {
      status: 'eligible',
      pincode: data.pincode,
      title: data.title,
      heroDate: heldEligible.heroDate,
      heroWhen: heldEligible.heroWhen,
      etaWindow: heldEligible.etaWindow ?? apiWindow ?? deliveryWindow,
      ...(heldEligible.heroTime ? { heroTime: heldEligible.heroTime } : {}),
      advisory: data.advisory ?? banner.advisory,
      mode: data.mode,
      city: data.city,
      cutoffIst: data.cutoff_ist,
      etaDate: data.eta_date,
      ...(data.order_by ? { orderBy: data.order_by } : {}),
      getItBy: heldEligible.checkoutEta,
      ...selection,
      ...windowFields,
    };
  }, [banner, phase, selectionBlocker, heldEligible, deliveryWindow]);

  useEffect(() => {
    if (shownRef.current) return;
    if (state.status === 'hidden') return;
    shownRef.current = true;
    pushToDataLayer(SDD_GTM_EVENTS.SHOWN, { status: state.status });
  }, [state.status]);

  return useMemo(
    () => ({
      state,
      startCheck,
      setPincodeInput,
      submitPincode,
      changePincode,
    }),
    [state, startCheck, setPincodeInput, submitPincode, changePincode],
  );
}
